/**
 * /app/pedidos/$nro — detalle full-page dentro del hub (no browser fullscreen).
 * $nro = order_number padded (0000001) o entero; también acepta id interno ord-… y redirige.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ClinicalPageShell } from "@/components/clinical";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AddOrderItemsDialog } from "@/components/orders/AddOrderItemsDialog";
import { PrintOrderDialog } from "@/components/orders/PrintOrderDialog";
import { ChangeCustomerDialog } from "@/components/orders/ChangeCustomerDialog";
import {
  OrderDetailPanel,
  formatOrderPesos,
  type OrderDetail,
} from "@/components/orders/OrderDetailPanel";
import { PosPagoModal } from "@/components/pos/PosPagoModal";
import { PosSaleInvoicePanel } from "@/components/pos/PosSaleInvoicePanel";
import { canAddItemsToOrder } from "@/lib/order-add-items";
import { orderCollectTotal } from "@/lib/pos-collect-order";
import { tryHipRoleAction } from "@/lib/hardware/client";
import {
  resolvePosInvoicingPolicy,
  defaultPosInvoiceMode,
  type PosInvoicingPolicy,
  type PosInvoiceMode,
} from "@/lib/pos/invoicing-policy";
import {
  formatOrderLabel,
  formatOrderNumber,
  looksLikeInternalOrderId,
  parseOrderNroParam,
} from "@/lib/order-number";
import { apiGetJson, apiSendJson, apiDelete } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useTenantContext } from "@/lib/tenant-context";
import {
  ORDER_STATUS_IMPACT,
  ORDER_STATUS_LABELS,
} from "@/domain/sm-models";
import type { OrderStatus } from "@/domain/sm-models";
import { ShipmentCreationFlowTour, LabelPrintingTour } from "@/components/ProductTour";

export const Route = createFileRoute("/app/pedidos_/$nro")({
  component: PedidoDetailPage,
  head: ({ params }) => ({
    meta: [{ title: `Orden ${params.nro} — Structura Market` }],
  }),
});

function PedidoDetailPage() {
  const { nro } = Route.useParams();
  const navigate = useNavigate();
  const tid = useWorkspaceTenantId();
  const { businessProfile, productFlags } = useTenantContext();

  const [detail, setDetail] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [pesoInput, setPesoInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [weighing, setWeighing] = useState(false);
  const [confirmTransition, setConfirmTransition] = useState<OrderStatus | null>(null);
  const [printDialog, setPrintDialog] = useState<{ type: "kitchen" | "receipt" | "exchange" } | null>(null);
  const [showCollectPago, setShowCollectPago] = useState(false);
  const [showAddItems, setShowAddItems] = useState(false);
  const [collectProcesando, setCollectProcesando] = useState(false);
  const [paymentTerminalBound, setPaymentTerminalBound] = useState(false);
  const [hasIssuedInvoice, setHasIssuedInvoice] = useState(false);
  const [showChangeCustomer, setShowChangeCustomer] = useState(false);
  const [invoicingPolicy, setInvoicingPolicy] = useState<PosInvoicingPolicy>("optional");
  const [invoiceMode, setInvoiceMode] = useState<PosInvoiceMode>("none");
  const [lastCollectedOrder, setLastCollectedOrder] = useState<{
    orderId: string;
    invoiceSummary?: string | null;
    customerEmail?: string | null;
    customerName?: string | null;
  } | null>(null);
  const invoicingEnabled =
    productFlags?.invoicing_afip_direct === true || productFlags?.invoicing_tusfacturas === true;

  const posCollectEnabled = productFlags?.payment_pos_mode === true;

  const load = useCallback(async () => {
    if (!tid) return;
    setLoading(true);
    setNotFound(false);
    try {
      let full: OrderDetail | null = null;
      if (looksLikeInternalOrderId(nro)) {
        full = await apiGetJson<OrderDetail>(`/api/orders?id=${encodeURIComponent(nro)}`, tid);
        if (full?.orderNumber != null) {
          void navigate({
            to: "/app/pedidos/$nro",
            params: { nro: formatOrderNumber(full.orderNumber) ?? String(full.orderNumber) },
            replace: true,
          });
          return;
        }
      } else {
        const num = parseOrderNroParam(nro);
        if (num == null) {
          setNotFound(true);
          setDetail(null);
          return;
        }
        full = await apiGetJson<OrderDetail>(`/api/orders?orderNumber=${num}`, tid);
        const canonical = formatOrderNumber(full.orderNumber);
        if (canonical && canonical !== nro) {
          void navigate({
            to: "/app/pedidos/$nro",
            params: { nro: canonical },
            replace: true,
          });
          return;
        }
      }
      setDetail(full);
      setPesoInput(full.pesoRealKg?.toString() ?? full.pesoEstimadoKg?.toString() ?? "");
      setNoteText(full.internalNotes ?? "");
    } catch {
      setNotFound(true);
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [tid, nro, navigate]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!tid) return;
    apiGetJson<{ bindings?: { paymentTerminal?: string } }>("/api/hardware/bindings", tid)
      .then((r) => setPaymentTerminalBound(Boolean(r.bindings?.paymentTerminal)))
      .catch(() => setPaymentTerminalBound(false));
  }, [tid]);

  // SCRUM-395: si ya hay comprobante fiscal emitido, no se puede cambiar el cliente.
  useEffect(() => {
    if (!tid || !detail) { setHasIssuedInvoice(false); return; }
    apiGetJson<{ invoices?: Array<{ status: string }> }>(
      `/api/tenant/invoices?orderId=${encodeURIComponent(detail.id)}&limit=5`,
      tid,
    )
      .then((r) => setHasIssuedInvoice((r.invoices ?? []).some((i) => i.status === "issued")))
      .catch(() => setHasIssuedInvoice(false));
  }, [tid, detail?.id]);

  async function handleChangeCustomer(customer: { id: string; name: string; email: string; phone: string | null }) {
    if (!tid || !detail) return;
    setSaving(true);
    try {
      const updated = await apiSendJson<OrderDetail & { loyaltyTransferred?: boolean }>(
        `/api/orders?id=${detail.id}`,
        "PATCH",
        { customerId: customer.id },
        tid,
      );
      toast.success(
        updated.loyaltyTransferred
          ? `Cliente cambiado a ${customer.name} — los puntos ganados se trasladaron`
          : `Cliente cambiado a ${customer.name}`,
      );
      setDetail((prev) => (prev ? { ...prev, ...updated } : prev));
      setShowChangeCustomer(false);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Error al cambiar el cliente");
    } finally {
      setSaving(false);
    }
  }

  async function handleChangePaymentMethod(method: string) {
    if (!tid || !detail) return;
    setSaving(true);
    try {
      const updated = await apiSendJson<OrderDetail>(
        `/api/orders?id=${detail.id}`,
        "PATCH",
        { paymentMethod: method },
        tid,
      );
      toast.success("Método de pago actualizado");
      setDetail((prev) => (prev ? { ...prev, ...updated } : prev));
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Error al cambiar el método de pago");
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    if (!tid) return;
    apiGetJson<{ config?: { storePolicies?: Record<string, unknown> } | null }>(
      "/api/tenant/config",
      tid,
    )
      .then((r) => {
        const policy = resolvePosInvoicingPolicy(r.config?.storePolicies ?? {});
        setInvoicingPolicy(policy);
        setInvoiceMode(defaultPosInvoiceMode(policy));
      })
      .catch(() => {});
  }, [tid]);

  async function handleTransition(newStatus: OrderStatus) {
    if (!tid || !detail) return;
    setSaving(true);
    try {
      const updated = await apiSendJson<OrderDetail>(
        `/api/orders?id=${detail.id}`,
        "PATCH",
        { status: newStatus },
        tid,
      );
      toast.success(
        newStatus === "CANCELADO"
          ? "Cancelado — stock revertido si correspondía"
          : `Estado: ${ORDER_STATUS_LABELS[newStatus]}`,
      );
      setDetail((prev) =>
        prev ? { ...prev, ...updated, items: updated.items?.length ? updated.items : prev.items } : prev,
      );
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Error al actualizar");
    } finally {
      setSaving(false);
      setConfirmTransition(null);
    }
  }

  async function handleAjustarPeso() {
    if (!tid || !detail) return;
    const kg = parseFloat(pesoInput);
    if (Number.isNaN(kg) || kg <= 0) {
      toast.error("Ingresá un peso válido mayor que 0");
      return;
    }
    setWeighing(true);
    try {
      const updated = await apiSendJson<OrderDetail>(
        "/api/pesaje",
        "POST",
        { action: "ajustar", orderId: detail.id, pesoRealKg: kg },
        tid,
      );
      toast.success(`Peso ajustado: ${kg} kg — Total: ${formatOrderPesos(updated.precioFinal)}`);
      setDetail((prev) =>
        prev ? { ...prev, ...updated, items: updated.items?.length ? updated.items : prev.items } : prev,
      );
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Error al ajustar peso");
    } finally {
      setWeighing(false);
    }
  }

  async function handleSaveNote() {
    if (!tid || !detail) return;
    setSaving(true);
    try {
      await apiSendJson(`/api/orders?id=${detail.id}`, "PATCH", { internalNotes: noteText }, tid);
      toast.success("Nota guardada");
      setDetail((prev) => (prev ? { ...prev, internalNotes: noteText } : prev));
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Error al guardar");
    } finally {
      setSaving(false);
    }
  }

  // SCRUM-525: editar/eliminar ítems existentes en órdenes no entregadas.
  async function handleUpdateItem(itemId: string, patch: { cantidad?: number; precioUnitario?: number }) {
    if (!tid || !detail) return;
    try {
      await apiSendJson(`/api/orders/items/${itemId}?orderId=${detail.id}`, "PATCH", patch, tid);
      toast.success("Ítem actualizado");
      await load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Error al editar el ítem");
    }
  }

  async function handleDeleteItem(itemId: string) {
    if (!tid || !detail) return;
    try {
      await apiDelete(`/api/orders/items/${itemId}?orderId=${detail.id}`, tid);
      toast.success("Ítem eliminado");
      await load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Error al eliminar el ítem");
    }
  }

  function openCollectAtPos() {
    setShowCollectPago(true);
  }

  async function handleCollectConfirm(payload: {
    metodo: string;
    montoPagado: number;
    vuelto: number;
    codigoGC?: string;
    loyaltyPoints?: number;
    customerId?: string;
    paymentMethods?: Array<{ method: string; amount: number }>;
    manualAck?: boolean;
    invoiceMode?: PosInvoiceMode;
  }) {
    if (!tid || !detail) return;
    setCollectProcesando(true);
    try {
      const sess = await apiGetJson<{ session?: { id: string }; _lock?: unknown }>(
        "/api/pos/session",
        tid,
      );
      if (sess._lock) {
        toast.error("POS no disponible en este plan");
        return;
      }
      const sessionId = sess.session?.id;
      if (!sessionId) {
        toast.error("Abrí una sesión de caja en POS para cobrar este retiro");
        return;
      }

      const r = await apiSendJson<{
        ok?: boolean;
        error?: string;
        invoice?: { comprobanteNro?: string | null; cae?: string | null; letra?: string | null } | null;
      }>("/api/pos/pay-order", "POST", {
        sessionId,
        orderId: detail.id,
        paymentMethods:
          payload.paymentMethods && payload.paymentMethods.length > 0
            ? payload.paymentMethods
            : [{ method: payload.metodo, amount: payload.montoPagado }],
        giftCardCode: payload.codigoGC || undefined,
        loyaltyPoints: payload.loyaltyPoints || undefined,
        customerId: payload.customerId || undefined,
        changeGiven: payload.vuelto,
        manualAck: payload.manualAck,
        invoiceMode: payload.invoiceMode ?? invoiceMode,
      }, tid);

      if (r.error) {
        toast.error(r.error);
        return;
      }

      toast.success("Cobro registrado en caja");
      setLastCollectedOrder({
        orderId: detail.id,
        invoiceSummary: r.invoice
          ? `Factura ${r.invoice.letra ?? ""}${r.invoice.comprobanteNro ? ` N° ${r.invoice.comprobanteNro}` : ""} emitida`.replace(/\s+/g, " ")
          : null,
        customerEmail: detail.customerEmail ?? null,
        customerName: detail.customerName ?? null,
      });
      setShowCollectPago(false);
      await load();
      if (payload.metodo === "efectivo") {
        void tryHipRoleAction(tid, "cashDrawer", "openDrawer");
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Error al cobrar");
    } finally {
      setCollectProcesando(false);
    }
  }

  function openSibling(orderId: string, orderNumber?: number | null) {
    if (orderNumber != null) {
      void navigate({
        to: "/app/pedidos/$nro",
        params: { nro: formatOrderNumber(orderNumber) ?? String(orderNumber) },
      });
      return;
    }
    void navigate({ to: "/app/pedidos/$nro", params: { nro: orderId } });
  }

  if (loading) {
    return (
      <ClinicalPageShell offset="none" className="pt-10 flex items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" /> Cargando orden…
      </ClinicalPageShell>
    );
  }

  if (notFound || !detail) {
    return (
      <ClinicalPageShell offset="none" className="pt-10 space-y-4 max-w-lg mx-auto text-center">
        <p className="text-sm text-muted-foreground">No encontramos esa orden.</p>
        <Button asChild variant="outline">
          <Link to="/app/pedidos">Volver a órdenes</Link>
        </Button>
      </ClinicalPageShell>
    );
  }

  return (
    <div className="flex flex-col gap-0 p-0">
      {detail.metodEntrega === "domicilio" ? (
        <>
          <ShipmentCreationFlowTour />
          <LabelPrintingTour />
        </>
      ) : null}

      {/* SCRUM-242: más ancho que antes (max-w-3xl) para el rail derecho de acciones. */}
      <ClinicalPageShell offset="none" className="pt-2 pb-8 max-w-6xl mx-auto w-full">
        <OrderDetailPanel
          layout="page"
          detail={detail}
          detailLoading={false}
          noteText={noteText}
          pesoInput={pesoInput}
          saving={saving}
          weighing={weighing}
          onNoteChange={setNoteText}
          onPesoChange={setPesoInput}
          onSaveNote={() => void handleSaveNote()}
          onAjustarPeso={() => void handleAjustarPeso()}
          onTransition={setConfirmTransition}
          onPrint={(type) => setPrintDialog({ type })}
          onCollectAtPos={openCollectAtPos}
          collectingAtPos={collectProcesando}
          posCollectEnabled={posCollectEnabled}
          onAddItems={() => setShowAddItems(true)}
          canAddItems={canAddItemsToOrder({ status: detail.status })}
          onUpdateItem={handleUpdateItem}
          onDeleteItem={handleDeleteItem}
          canEditItems={canAddItemsToOrder({ status: detail.status })}
          businessProfile={businessProfile}
          tenantId={tid}
          onOpenSibling={(id) => {
            const sib = detail.splitSiblings?.find((s) => s.id === id);
            openSibling(id, sib?.orderNumber);
          }}
          hasIssuedInvoice={hasIssuedInvoice}
          onChangeCustomer={() => setShowChangeCustomer(true)}
          onChangePaymentMethod={(method) => void handleChangePaymentMethod(method)}
          headerStart={
            <Button variant="ghost" size="icon" className="shrink-0 mt-0.5" asChild>
              <Link to="/app/pedidos" aria-label="Volver a órdenes">
                <ArrowLeft className="h-5 w-5" />
              </Link>
            </Button>
          }
        />
      </ClinicalPageShell>

      {tid ? (
        <ChangeCustomerDialog
          open={showChangeCustomer}
          onOpenChange={setShowChangeCustomer}
          tenantId={tid}
          onSelect={(c) => void handleChangeCustomer(c)}
        />
      ) : null}

      {printDialog ? (
        <PrintOrderDialog
          open={!!printDialog}
          onOpenChange={(v) => {
            if (!v) setPrintDialog(null);
          }}
          orderId={detail.id}
          type={printDialog.type}
          tid={tid ?? ""}
          onPrinted={() => {
            if (tid) void load();
          }}
        />
      ) : null}

      {showAddItems && tid ? (
        <AddOrderItemsDialog
          open={showAddItems}
          onOpenChange={setShowAddItems}
          tenantId={tid}
          orderId={detail.id}
          onAdded={async ({ amountDue }) => {
            await load();
            setPrintDialog({ type: "kitchen" });
            if (amountDue >= 0.01) {
              toast.message(`Saldo a cobrar: $${amountDue.toLocaleString("es-AR")}`);
            }
          }}
        />
      ) : null}

      {showCollectPago && tid ? (
        <PosPagoModal
          total={orderCollectTotal(detail)}
          tenantId={tid}
          flags={productFlags ?? {}}
          paymentTerminalBound={paymentTerminalBound}
          procesando={collectProcesando}
          orderId={detail.id}
          description={`Cobro ${formatOrderLabel(detail.orderNumber)}`}
          invoiceMode={invoiceMode}
          onInvoiceModeChange={setInvoiceMode}
          invoicingPolicy={invoicingPolicy}
          showInvoicing
          onClose={() => {
            if (!collectProcesando) setShowCollectPago(false);
          }}
          onConfirm={(p) => void handleCollectConfirm(p)}
        />
      ) : null}

      <Dialog
        open={Boolean(lastCollectedOrder)}
        onOpenChange={(open) => {
          if (!open) setLastCollectedOrder(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cobro registrado</DialogTitle>
          </DialogHeader>
          {lastCollectedOrder && tid ? (
            invoicingEnabled ? (
              <PosSaleInvoicePanel
                tenantId={tid}
                orderId={lastCollectedOrder.orderId}
                policy={invoicingPolicy}
                initialSummary={lastCollectedOrder.invoiceSummary}
                customerEmail={lastCollectedOrder.customerEmail}
                customerName={lastCollectedOrder.customerName}
              />
            ) : (
              <p className="text-sm text-muted-foreground">El pedido quedó cobrado.</p>
            )
          ) : null}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmTransition} onOpenChange={(open) => !open && setConfirmTransition(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cambiar estado</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>
                  ¿Confirmás el cambio a{" "}
                  <strong>{confirmTransition ? ORDER_STATUS_LABELS[confirmTransition] : ""}</strong>?
                </p>
                {confirmTransition ? (
                  <p className="mt-2 text-xs text-muted-foreground">{ORDER_STATUS_IMPACT[confirmTransition]}</p>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmTransition && void handleTransition(confirmTransition)}
              className={
                confirmTransition === "CANCELADO"
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : ""
              }
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

